import { ChatMarkdown, IconButton } from '@wundo/ui';
import { FileText, Mic, Trash2 } from 'lucide-react';
import type { MeetingPageMaterial } from '@wundo/shared/meetingPage.ts';
import { m } from '@/paraglide/messages.js';

export interface MeetingMaterialListProps {
  materials: MeetingPageMaterial[];
  onDelete: (materialId: string) => void;
}

/**
 * What this meeting has to write a verslag from, as rows.
 *
 * The notetaker's transcript is listed as a fact and nothing else: the user
 * sees that it exists and was used, never a word of it. Their own notes are
 * shown as they wrote them — wrapped, line breaks kept, cut off after a few
 * lines: your own account of a conversation has to be readable on the page,
 * not squeezed into one clipped line.
 */
export function MeetingMaterialList({ materials, onDelete }: MeetingMaterialListProps) {
  if (materials.length === 0) return null;

  return (
    <ul className='flex flex-col'>
      {materials.map((material) =>
        material.kind === 'transcript' ? (
          <li key='transcript' className='flex items-center gap-3 py-1.5'>
            <Mic className='size-4 text-ink-muted' aria-hidden='true' />
            <span className='text-base text-ink-body'>
              {m.meeting_report_material_transcript()}
            </span>
          </li>
        ) : (
          <li key={material.id} className='group flex items-start gap-3 py-1.5'>
            <FileText
              className='mt-1 size-4 shrink-0 text-ink-muted'
              aria-hidden='true'
            />
            {/*
              The note as the user wrote it: rendered, not raw — the debrief
              writes headings and lists, and showing their markup would be
              showing plumbing. Clamped to a handful of lines so a long paste
              cannot push the verslag off the page; the whole note is a click
              away in the edit dialog, which is why cutting here costs nothing.
            */}
            <ChatMarkdown className='min-w-0 flex-1 line-clamp-6 text-base text-ink-body'>
              {material.excerpt}
            </ChatMarkdown>
            <IconButton
              size='sm'
              variant='danger'
              aria-label={m.meeting_report_material_delete()}
              className='shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100'
              onClick={() => onDelete(material.id)}
            >
              <Trash2 className='size-4' aria-hidden='true' />
            </IconButton>
          </li>
        ),
      )}
    </ul>
  );
}
